import { Link } from "react-router-dom";
import { ArrowUpRight, Truck, Building2, ShieldCheck, Handshake, Award } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const divisions = [
  {
    no: "01",
    title: "Tandem T Logistics",
    desc: "Freight, transportation and supply-chain solutions engineered for high-velocity, multi-state commerce.",
    to: "/logistics",
    icon: Truck,
    testId: "home-card-logistics",
  },
  {
    no: "02",
    title: "Tandem T Properties",
    desc: "Industrial real estate and infrastructure assets — built, acquired and operated with institutional discipline.",
    to: "/properties",
    icon: Building2,
    testId: "home-card-properties",
  },
  {
    no: "03",
    title: "Government Contracting",
    desc: "Federal compliance, set-aside readiness and capabilities aligned to mission-critical agency requirements.",
    to: "/government",
    icon: ShieldCheck,
    testId: "home-card-government",
  },
  {
    no: "04",
    title: "Partnerships",
    desc: "Strategic alliances with primes, municipalities and community institutions that compound enterprise value.",
    to: "/partnerships",
    icon: Handshake,
    testId: "home-card-partnerships",
  },
];

const stats = [
  { k: "Majority", v: "Minority Women-Owned" },
  { k: "Multi-State", v: "Operational Footprint" },
  { k: "Federal", v: "Compliance Ready" },
  { k: "Generational", v: "Value Mandate" },
];

export default function Home() {
  useSEO({
    title: "Tandem T Holdings LLC — Logistics · Real Estate · Government",
    description: "A minority woman-owned holding company delivering innovative logistics, transportation, and real estate solutions across Tandem T Logistics, Tandem T Properties, and government-aligned services. Founded by Chrissy Thompson.",
    path: "/",
  });
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end pt-28 pb-16 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1527576539890-dfa815648363"
            alt="Architecture lines"
            className="w-full h-full object-cover bw-img opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black" />
          <div className="absolute inset-0 grain" />
        </div>

        {/* HERO WATERMARK — top-right, contained so it never overlaps the quote */}
        <div className="absolute right-[-2%] top-[6%] w-[38%] max-w-[520px] opacity-[0.14] pointer-events-none select-none hidden md:block" aria-hidden>
          <img src="/brand/tandem-t-logo.png" alt="" className="w-full h-auto" style={{ mixBlendMode: "screen" }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Reveal>
              <p className="overline text-white/65">A Majority Minority Women-Owned Holding Company</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-serif font-light text-white text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight mt-8">
                Sustainable <em className="italic text-white/85">growth.</em><br />
                Generational <em className="italic text-white/85">value.</em>
              </h1>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="font-sans font-light text-white/75 text-base md:text-lg mt-10 max-w-2xl leading-relaxed">
                Tandem T Holdings LLC delivers innovative logistics, transportation and real estate solutions
                that strengthen businesses, support government initiatives, and create opportunities within
                the communities we serve.
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="flex flex-wrap gap-4 mt-12">
                <Link
                  data-testid="hero-cta-contact"
                  to="/contact"
                  className="group inline-flex items-center gap-3 px-7 py-4 bg-white text-black overline hover:bg-white/85 transition-colors"
                >
                  Engage Tandem T
                  <ArrowUpRight size={16} className="hover-arrow" />
                </Link>
                <Link
                  data-testid="hero-cta-divisions"
                  to="/government"
                  className="group inline-flex items-center gap-3 px-7 py-4 border border-white/30 text-white overline hover:bg-white hover:text-black transition-colors"
                >
                  Government Capabilities
                  <ArrowUpRight size={16} className="hover-arrow" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-4 md:mt-64 lg:mt-80 xl:mt-96">
            <Reveal delay={0.4}>
              <div className="border-l border-white/20 pl-6 space-y-6">
                <p className="overline text-white/55">Est. Holding Group</p>
                <p className="font-serif italic text-white/80 text-xl leading-snug">
                  "We build the infrastructure of opportunity — quietly, deliberately, and with uncompromising
                  integrity."
                </p>
                <p className="overline text-white/55">— Office of the CEO</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4">
          {stats.map((s, i) => (
            <div key={i} className={`px-6 md:px-10 py-10 ${i !== 0 ? "md:border-l border-white/10" : ""} ${i < 2 ? "border-b md:border-b-0 border-white/10" : ""}`}>
              <p className="font-serif text-3xl md:text-4xl text-white font-light">{s.k}</p>
              <p className="overline text-white/55 mt-3">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
            <div className="md:col-span-7">
              <Reveal><p className="overline text-white/55">The Holding Group</p></Reveal>
              <Reveal delay={0.08}>
                <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-tight tracking-tight">
                  Four divisions. <em>One discipline.</em>
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5">
              <Reveal delay={0.16}>
                <p className="text-white/65 font-light leading-relaxed">
                  Each Tandem T entity is engineered to operate independently — and integrate seamlessly —
                  unlocking compounding leverage across logistics, real estate, government and community.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {divisions.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal key={d.title} delay={i * 0.08} className="bg-black">
                  <Link
                    to={d.to}
                    data-testid={d.testId}
                    className="group block p-10 md:p-14 min-h-[300px] hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <span className="overline text-white/40">{d.no}</span>
                      <Icon size={22} className="text-white/65 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-serif font-light text-white text-3xl md:text-4xl mt-10 leading-tight">{d.title}</h3>
                    <p className="text-white/65 font-light leading-relaxed mt-5 max-w-md">{d.desc}</p>
                    <span className="inline-flex items-center gap-3 overline text-white mt-10">
                      Explore <span className="hover-arrow">→</span>
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CEO FEATURE */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <Reveal className="md:col-span-4">
            <div className="relative max-w-[360px]">
              <img
                src="/images/chrissy-thompson.png"
                alt="Chrissy Thompson, CEO"
                className="w-full aspect-[4/5] object-contain object-center bw-img bg-black"
                data-testid="ceo-portrait"
              />
              <div className="absolute -bottom-3 -right-3 w-full h-full border border-white/20 -z-10" />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-8">
            <p className="overline text-white/55">Office of the CEO</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-5 leading-tight tracking-tight">
              Chrissy Thompson
            </h2>
            <p className="overline text-white/55 mt-3">President & Chief Executive Officer</p>
            <p className="text-white/75 font-light leading-relaxed mt-8 text-lg">
              Driven by operational excellence and uncompromising integrity, Chrissy Thompson leverages
              her strategic vision as CEO to orchestrate Tandem T Holdings LLC into an elite, multi-state
              corporate force bridging freight logistics and industrial real estate infrastructure.
            </p>
            <p className="text-white/65 font-light leading-relaxed mt-5">
              As a powerhouse majority stakeholder, she masterfully navigates high-level federal compliance
              and supplier diversity frameworks to unlock maximum asset protection, generate high-velocity
              funding potential, and build non-dilutable enterprise value for future generations.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <span className="overline px-4 py-2 border border-white/25">Federal Compliance</span>
              <span className="overline px-4 py-2 border border-white/25">Asset Strategy</span>
              <span className="overline px-4 py-2 border border-white/25">Supplier Diversity</span>
            </div>
            <Link
              to="/letter"
              data-testid="home-cta-letter"
              className="group inline-flex items-center gap-3 mt-10 overline px-5 py-3 border border-white/30 text-white hover:bg-white hover:text-black transition-colors"
            >
              Read the Founders&rsquo; Letter <ArrowUpRight size={14} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SUPPLIER DIVERSITY STRIP */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <Award size={26} className="text-white" />
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
              A proudly <em className="italic">Black Woman-Owned</em> enterprise.
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-6 max-w-lg">
              Tandem T Holdings is built on a mandate of representation, opportunity and economic legacy —
              powering supplier diversity outcomes for the partners and agencies we serve.
            </p>
            <Link
              data-testid="home-diversity-cta"
              to="/diversity"
              className="group inline-flex items-center gap-3 mt-10 overline px-5 py-3 bg-white text-black hover:bg-white/85 transition-colors"
            >
              Supplier Diversity Profile
              <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-6">
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {["MWBE", "WOSB", "DBE", "8(a)-Eligible"].map((b, i) => (
                <div key={b} className="bg-black p-8 md:p-10 text-center">
                  <p className="font-serif text-white text-3xl md:text-4xl font-light">{b}</p>
                  <p className="overline text-white/55 mt-3">Pursuing / Aligned</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
